import React, { useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Page from "../components/Page";
import {
  IconCalendarPlus,
  IconCalendarTime,
  IconEdit,
  IconMail,
  IconPencil,
  IconPhone,
  IconPlus,
  IconTrash,
  IconUser,
  IconX,
  IconSearch,
  IconReceipt,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import { useSuppliers, addSupplier, updateSupplier, deleteSupplier } from "../controllers/inventory.controller";
import { clsx } from "clsx";
import { toast } from "react-hot-toast";
import { useTheme } from "../contexts/ThemeContext";

export default function SuppliersPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const nameRef = useRef();
  const contactPersonRef = useRef();
  const phoneRef = useRef();
  const emailRef = useRef();
  const addressRef = useRef();
  const openingBalanceRef = useRef();
  const panNoRef = useRef();
  const isVatRegisteredRef = useRef();

  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(18);

  const { data: suppliers, isLoading, error, mutate } = useSuppliers();

  const filteredSuppliers = useMemo(() => {
    if (!suppliers || !Array.isArray(suppliers)) return [];
    if (!searchQuery.trim()) return suppliers;
    const q = searchQuery.toLowerCase().trim();
    return suppliers.filter((s) => {
      const name = (s.name || "").toLowerCase();
      const contact = (s.contact_person || "").toLowerCase();
      const phone = (s.phone || "").toLowerCase();
      const email = (s.email || "").toLowerCase();
      const pan = (s.pan_no || "").toLowerCase();
      const address = (s.address || "").toLowerCase();
      return name.includes(q) || contact.includes(q) || phone.includes(q) || email.includes(q) || pan.includes(q) || address.includes(q);
    });
  }, [suppliers, searchQuery]);

  const totalPages = Math.max(1, Math.ceil((filteredSuppliers?.length || 0) / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedSuppliers = useMemo(() => {
    return filteredSuppliers.slice((safeCurrentPage - 1) * pageSize, safeCurrentPage * pageSize);
  }, [filteredSuppliers, safeCurrentPage, pageSize]);

  if (isLoading) {
    return <Page>Loading suppliers...</Page>;
  }

  if (error) {
    return <Page>Error loading suppliers.</Page>;
  }

  const btnAdd = async () => {
    const name = nameRef.current.value;
    const contact_person = contactPersonRef.current.value;
    const phone = phoneRef.current.value;
    const email = emailRef.current.value;
    const address = addressRef.current.value;
    const openingBalance = openingBalanceRef.current.value || 0;
    const panNo = panNoRef.current.value || null;
    const isVatRegistered = isVatRegisteredRef.current.checked || false;

    if (!name || !phone) {
      toast.error("Name and Phone are required!");
      return;
    }

    try {
      toast.loading("Adding supplier...");
      const res = await addSupplier({ name, contact_person, phone, email, address, openingBalance, panNo, isVatRegistered });
      toast.dismiss();
      if (res.status === 200 || res.status === 201) {
        toast.success(res.data.message || "Supplier added successfully");
        mutate();
        document.getElementById("modal-supplier").close();
        // clear refs
        nameRef.current.value = "";
        contactPersonRef.current.value = "";
        phoneRef.current.value = "";
        emailRef.current.value = "";
        addressRef.current.value = "";
        openingBalanceRef.current.value = "";
        panNoRef.current.value = "";
        isVatRegisteredRef.current.checked = false;
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Error adding supplier");
    }
  };

  const btnUpdate = async () => {
    const name = nameRef.current.value;
    const contact_person = contactPersonRef.current.value;
    const phone = phoneRef.current.value;
    const email = emailRef.current.value;
    const address = addressRef.current.value;
    const openingBalance = openingBalanceRef.current.value || 0;
    const panNo = panNoRef.current.value || null;
    const isVatRegistered = isVatRegisteredRef.current.checked || false;

    try {
      toast.loading("Updating supplier...");
      const res = await updateSupplier(selectedSupplierId, { name, contact_person, phone, email, address, openingBalance, panNo, isVatRegistered });
      toast.dismiss();
      if (res.status === 200) {
        toast.success(res.data.message);
        mutate();
        document.getElementById("modal-supplier").close();
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Error updating supplier");
    }
  };

  const btnDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      toast.loading("Deleting supplier...");
      const res = await deleteSupplier(id);
      if (res.status === 200 || res.status === 204) {
        toast.dismiss();
        toast.success(res.data?.message || "Supplier deleted successfully");
        mutate();
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Error deleting supplier");
    }
  };

  const btnShowAdd = () => {
    setSelectedSupplierId(null);
    nameRef.current.value = "";
    contactPersonRef.current.value = "";
    phoneRef.current.value = "";
    emailRef.current.value = "";
    addressRef.current.value = "";
    openingBalanceRef.current.value = "";
    panNoRef.current.value = "";
    isVatRegisteredRef.current.checked = false;
    document.getElementById("modal-supplier").showModal();
  };

  const btnShowUpdate = (supplier) => {
    setSelectedSupplierId(supplier.id);
    nameRef.current.value = supplier.name;
    contactPersonRef.current.value = supplier.contact_person || "";
    phoneRef.current.value = supplier.phone || "";
    emailRef.current.value = supplier.email || "";
    addressRef.current.value = supplier.address || "";
    openingBalanceRef.current.value = supplier.opening_balance || 0;
    panNoRef.current.value = supplier.pan_no || "";
    isVatRegisteredRef.current.checked = supplier.is_vat_registered ? true : false;
    document.getElementById("modal-supplier").showModal();
  };

  return (
    <Page>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-2xl font-bold">Suppliers</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage your vendors, contacts, opening balances, and view transaction ledgers
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search supplier, phone, PAN..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="input input-sm input-bordered rounded-lg pl-9 w-52 md:w-64"
            />
          </div>

          <Link
            to="/dashboard/inventory/accounts-payable"
            className="btn btn-sm btn-outline border-gray-300 dark:border-gray-700 flex items-center gap-1 font-bold"
            title="View Accounts Payable and Settle Vendor Dues"
          >
            <IconReceipt size={16} /> Payables / Ledger
          </Link>

          <button onClick={btnShowAdd} className="btn btn-primary btn-sm flex items-center gap-1">
            <IconPlus size={18} /> New Supplier
          </button>
        </div>
      </div>

      {(!paginatedSuppliers || paginatedSuppliers.length === 0) && (
        <div className="py-20 text-center bg-white dark:bg-restro-gray border border-dashed border-gray-200 dark:border-gray-800 rounded-2xl">
          <IconUser size={40} className="mx-auto text-gray-400 mb-2" />
          <p className="text-gray-500 font-medium">
            {searchQuery ? "No suppliers match your search query." : "No suppliers added yet."}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {paginatedSuppliers.map((supplier) => (
          <div key={supplier.id} className="border rounded-2xl p-4 border-restro-border-green bg-restro-gray flex flex-col justify-between shadow-sm">
            <div>
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-restro-bg-gray flex items-center justify-center font-bold text-gray-700 dark:text-gray-200">
                    {supplier.name?.[0]?.toUpperCase() || <IconUser size={20} />}
                  </div>
                  <div>
                    <h4 className="font-bold leading-tight truncate max-w-[160px]" title={supplier.name}>{supplier.name}</h4>
                    <p className="text-xs text-gray-500">{supplier.contact_person || "No contact person"}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => btnShowUpdate(supplier)} className="p-1.5 hover:bg-restro-button-hover rounded-full transition-colors" title="Edit">
                    <IconPencil size={16} />
                  </button>
                  <button onClick={() => btnDelete(supplier.id)} className="p-1.5 hover:bg-restro-button-hover rounded-full text-red-500 transition-colors" title="Delete">
                    <IconTrash size={16} />
                  </button>
                </div>
              </div>
              <div className="space-y-1 text-sm mt-3">
                <p className="flex items-center gap-2 text-xs"><IconPhone size={14} className="text-gray-400" /> {supplier.phone}</p>
                {supplier.email && <p className="flex items-center gap-2 text-xs"><IconMail size={14} className="text-gray-400" /> {supplier.email}</p>}
                {supplier.pan_no && <p className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-300">PAN/VAT: {supplier.pan_no} {supplier.is_vat_registered ? "(VAT)" : ""}</p>}
                {supplier.opening_balance !== undefined && <p className="text-xs font-semibold mt-1 text-gray-600 dark:text-gray-300">Opening Balance: {Number(String(supplier.opening_balance).replace(/,/g, "")).toFixed(2)}</p>}
                {supplier.address && <p className="text-xs text-gray-400 mt-1 truncate" title={supplier.address}>{supplier.address}</p>}
              </div>
            </div>

            <div className="pt-3 mt-3 border-t border-gray-100 dark:border-gray-800 flex justify-end">
              <Link
                to={`/dashboard/inventory/accounts-payable?supplierId=${supplier.id}`}
                className="btn btn-xs btn-outline border-gray-300 dark:border-gray-700 flex items-center gap-1 font-semibold"
                title="View Ledger Statement and Clear Dues"
              >
                <IconReceipt size={14} /> Ledger / Dues
              </Link>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination controls */}
      {filteredSuppliers?.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 p-4 mt-6 border border-gray-100 dark:border-gray-800 rounded-2xl bg-white dark:bg-restro-gray">
          <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
            <span>
              Showing {((safeCurrentPage - 1) * pageSize) + 1} to {Math.min(safeCurrentPage * pageSize, filteredSuppliers.length)} of {filteredSuppliers.length} suppliers
            </span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="select select-xs select-bordered rounded-lg bg-white dark:bg-restro-gray ml-2"
            >
              <option value={9}>9 / page</option>
              <option value={18}>18 / page</option>
              <option value={36}>36 / page</option>
              <option value={90}>90 / page</option>
            </select>
          </div>
          <div className="join">
            <button
              className="join-item btn btn-xs"
              disabled={safeCurrentPage === 1}
              onClick={() => setCurrentPage(1)}
              title="First Page"
            >
              <IconChevronsLeft size={14} />
            </button>
            <button
              className="join-item btn btn-xs"
              disabled={safeCurrentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              title="Previous Page"
            >
              <IconChevronLeft size={14} />
            </button>
            <button className="join-item btn btn-xs no-animation font-semibold">
              Page {safeCurrentPage} of {totalPages}
            </button>
            <button
              className="join-item btn btn-xs"
              disabled={safeCurrentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              title="Next Page"
            >
              <IconChevronRight size={14} />
            </button>
            <button
              className="join-item btn btn-xs"
              disabled={safeCurrentPage >= totalPages}
              onClick={() => setCurrentPage(totalPages)}
              title="Last Page"
            >
              <IconChevronsRight size={14} />
            </button>
          </div>
        </div>
      )}

      <dialog id="modal-supplier" className="modal">
        <div className="modal-box border border-restro-border-green dark:rounded-2xl">
          <h3 className="font-bold text-lg">{selectedSupplierId ? "Update Supplier" : "Add Supplier"}</h3>
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm text-gray-500 mb-1">Supplier Name *</label>
              <input ref={nameRef} type="text" className="input input-bordered w-full" placeholder="e.g. Acme Corp" />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Contact Person</label>
              <input ref={contactPersonRef} type="text" className="input input-bordered w-full" placeholder="e.g. John Doe" />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Phone *</label>
              <input ref={phoneRef} type="tel" className="input input-bordered w-full" placeholder="Phone number" />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Email</label>
              <input ref={emailRef} type="email" className="input input-bordered w-full" placeholder="Email address" />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Address</label>
              <textarea ref={addressRef} className="textarea textarea-bordered w-full" placeholder="Full address"></textarea>
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Opening Balance</label>
              <input ref={openingBalanceRef} type="number" step="0.01" className="input input-bordered w-full" placeholder="0.00" />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">PAN/VAT Number</label>
              <input ref={panNoRef} type="text" className="input input-bordered w-full" placeholder="Supplier PAN or VAT Number" />
            </div>
            <div className="flex items-center gap-2 mt-4">
              <input type="checkbox" ref={isVatRegisteredRef} className="toggle toggle-success" />
              <span className="label-text text-sm">Is VAT Registered?</span>
            </div>
          </div>
          <div className="modal-action">
            <form method="dialog">
              <button className="btn">Close</button>
              <button type="button" onClick={selectedSupplierId ? btnUpdate : btnAdd} className="btn btn-primary ml-2">
                {selectedSupplierId ? "Update" : "Add"}
              </button>
            </form>
          </div>
        </div>
      </dialog>
    </Page>
  );
}
